import { observable, makeObservable, actionBound } from "mobx";
import { ManualOrderItem } from "./type";
import { cloneDeep, uniqBy } from "lodash";
import { Modal } from "antd";

function deleteManualOrderItemKey(manualOrderItems: ManualOrderItem[]) {
    const cloneManualOrderItems = cloneDeep(manualOrderItems);

    cloneManualOrderItems?.forEach((it) => {
        delete it.key;
    });

    return cloneManualOrderItems;
}

const cache = "___HistoryAddedManualOrderItemListCache__";

const MAX_CACHE_HISTORY_ORDER_ITEM_SIZE = 20;

/** 根据标题+属性确认唯一的ManualOrderItem */
function getUniqHistoryAddedManualOrderItemList(manualOrderItems: ManualOrderItem[]) {
    if (!Array.isArray(manualOrderItems)) return [];

    const cloneManualOrderItems = cloneDeep(manualOrderItems);

    cloneManualOrderItems.forEach((cloneManualOrderItem) => {
        delete cloneManualOrderItem["key"];
        delete cloneManualOrderItems["selectedKey"];
    });

    return uniqBy(cloneManualOrderItems, ({ title, skuProperties }) => {
        const uniqFlag = (title ?? "") + (skuProperties ?? "");

        return uniqFlag;
    });
}

class OrderItemFormStore {
    constructor() {
        makeObservable(this, {
            orderItems: observable,
            selectedRowKeys: observable,
            historyAddedManualOrderItemList: observable,
            initHistoryAddedManualOrderItems: actionBound,
            handleRemoveHistoryOrderItem: actionBound,
            addHistoryAddedManualOrderItems: actionBound,
            setSelectedRowKeys: actionBound,
            addOrderItems: actionBound,
            updateOrderItem: actionBound,
            deleteOrderItem: actionBound,
            getSelectedOrderItems: actionBound,
            getOrderItems: actionBound,
            setOrderItems: actionBound,
            reset: actionBound,
        });
        this.initHistoryAddedManualOrderItems();
    }

    orderItems: ManualOrderItem[] = [];

    /** 选中的OrderItems(快速查单中使用) */
    selectedRowKeys: string[] = [];

    /** 添加过的ManualOrderItem,存放在缓存中 */
    historyAddedManualOrderItemList: ManualOrderItem[] = [];

    initHistoryAddedManualOrderItems() {
        try {
            const cacheData = localStorage.getItem(cache);
            const cacheHistoryAddedManualOrderItemList: ManualOrderItem[] = JSON.parse(cacheData);

            this.historyAddedManualOrderItemList = getUniqHistoryAddedManualOrderItemList(
                cacheHistoryAddedManualOrderItemList,
            );
        } catch {}
    }

    handleRemoveHistoryOrderItem(index: number) {
        const nextHistoryOrderItemList = this.historyAddedManualOrderItemList.filter((_, i) => i !== index);

        this.historyAddedManualOrderItemList = nextHistoryOrderItemList;

        const historyAddedManualOrderItemListStr = JSON.stringify(nextHistoryOrderItemList);

        localStorage.setItem(cache, historyAddedManualOrderItemListStr);
    }

    addHistoryAddedManualOrderItems(historyAddedManualOrderItemList: ManualOrderItem[]) {
        /** 将最新添加的放到前面 */
        const uniqHistoryAddedManualOrderItemList = getUniqHistoryAddedManualOrderItemList([
            ...historyAddedManualOrderItemList,
            ...this.historyAddedManualOrderItemList,
        ]);

        /** 移除超过最大长度的部分 */
        const totalLength = uniqHistoryAddedManualOrderItemList.length;
        if (totalLength > MAX_CACHE_HISTORY_ORDER_ITEM_SIZE) {
            const overflowCount = totalLength - MAX_CACHE_HISTORY_ORDER_ITEM_SIZE;
            uniqHistoryAddedManualOrderItemList.splice(-overflowCount);
        }

        this.historyAddedManualOrderItemList = uniqHistoryAddedManualOrderItemList;

        const historyAddedManualOrderItemListStr = JSON.stringify(uniqHistoryAddedManualOrderItemList);

        localStorage.setItem(cache, historyAddedManualOrderItemListStr);
    }

    setSelectedRowKeys(selectedRowKeys: string[]) {
        this.selectedRowKeys = selectedRowKeys;
    }

    addOrderItems(orderItems: ManualOrderItem[]) {
        this.orderItems = [...this.orderItems, ...orderItems];
    }

    updateOrderItem(orderItem: ManualOrderItem) {
        const index = this.orderItems.findIndex(({ key }) => key === orderItem.key);

        if (index >= 0) {
            const nextOrderItems = [...this.orderItems];
            nextOrderItems[index] = orderItem;
            this.orderItems = nextOrderItems;
        }
    }

    deleteOrderItem(index: number) {
        const nextOrderItems = [...this.orderItems];

        nextOrderItems.splice(index, 1);

        this.orderItems = nextOrderItems;
    }

    getSelectedOrderItems() {
        // 这里需要过滤掉没有选中的行
        const orderItems = this.getOrderItems().filter(({ key }) => this.selectedRowKeys.includes(key));

        return deleteManualOrderItemKey(orderItems);
    }

    getOrderItems() {
        return this.orderItems;
    }

    setOrderItems(orderItems: ManualOrderItem[]) {
        this.orderItems = orderItems;
    }

    reset() {
        this.orderItems = [];
    }
}

export default OrderItemFormStore;
